import { Navigate, Routes, Route, useLocation } from "react-router-dom";
import React, { Suspense, lazy, useCallback, useEffect, useState } from "react";

import { useSwipeable } from "react-swipeable";

import Cookies from "js-cookie";

import Loader from "./components/Loader/Loader";
import SeoHead from "./components/Seo/SeoHead";
import api from "./util/api";
import { ENABLE_DASHBOARD_PAGE } from "./config/featureFlags";
import {
  getSessionGateRoute,
  isAuthenticatedUserSession,
} from "./util/sessionAuth";

const LandingPage = lazy(() => import("./pages/LandingPage/LandingPage"));
const AddressMigrationPreview = import.meta.env.DEV
  ? lazy(() => import("./pages/AddressMigrationPreview/AddressMigrationPreview"))
  : null;
const OrbitalDemo = lazy(() => import("./pages/OrbitalDemo/OrbitalDemo"));
const ConstellationDemo = lazy(() =>
  import("./pages/ConstellationDemo/ConstellationDemo")
);
const Login = lazy(() => import("./pages/Login/Login"));
const Logout = lazy(() => import("./pages/Login/Logout"));
const Signup = lazy(() => import("./pages/Login/Signup"));
const TermsPage = lazy(() => import("./pages/Legal/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/Legal/PrivacyPage"));
const BlingError = lazy(() => import("./pages/Errors/BlingError"));
const Purchase = lazy(() => import("./pages/Purchase/Purchase"));
const ExternalCatalog = lazy(() => import("./pages/ExternalCatalog/ExternalCatalog"));
const ZPLuma = lazy(() => import("./pages/ZPLuma/ZPLuma"));
const AdminLogin = lazy(() => import("./pages/Admin/Login/Login"));
const Maintenance = lazy(() => import("./pages/Maintenance/Maintenance"));
const AdminDomainRoutes = lazy(() => import("./routes/AdminDomainRoutes"));
const OperationsTvDashboard = lazy(
  () => import("./pages/Admin/OperationsTvDashboard/OperationsTvDashboard")
);
const UserDomainRoutes = lazy(() => import("./routes/UserDomainRoutes"));

const maintenance = false;
const DEFAULT_AUTHENTICATED_ROUTE = ENABLE_DASHBOARD_PAGE
  ? "/dashboard"
  : "/produtos";

const SCROLLABLE_OVERFLOW_VALUES = new Set(["auto", "scroll", "overlay"]);

const hasScrollableOverflow = (element) => {
  if (!(element instanceof Element) || typeof window === "undefined") {
    return false;
  }

  const styles = window.getComputedStyle(element);
  const canScrollY =
    SCROLLABLE_OVERFLOW_VALUES.has(styles.overflowY) &&
    element.scrollHeight > element.clientHeight + 1;
  const canScrollX =
    SCROLLABLE_OVERFLOW_VALUES.has(styles.overflowX) &&
    element.scrollWidth > element.clientWidth + 1;

  return canScrollY || canScrollX;
};

const isInsideScrollableContainer = (event) => {
  if (typeof document === "undefined") return false;

  let node = event?.target;
  while (node && node !== document.body && node !== document.documentElement) {
    if (hasScrollableOverflow(node)) return true;
    node = node.parentElement;
  }

  return false;
};

function RouteLoader() {
  return (
    <div className="auth-loader">
      <Loader />
    </div>
  );
}

const getSessionErrorCode = (error) =>
  error?.data?.code || error?.data?.session?.code || error?.code;

function RootRoute() {
  const [target, setTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadSession = async () => {
      try {
        const session = await api.get("/api/session", { timeout: 8000 });
        if (cancelled) return;

        if (!isAuthenticatedUserSession(session)) {
          setTarget("landing");
          return;
        }

        const gateRoute = getSessionGateRoute(session);
        if (gateRoute) {
          setTarget(gateRoute);
          return;
        }

        setTarget(DEFAULT_AUTHENTICATED_ROUTE);
      } catch (error) {
        if (cancelled) return;

        const code = getSessionErrorCode(error);
        if (code === "PAYMENT") {
          setTarget("/pagamento");
          return;
        }

        setTarget("landing");
      }
    };

    loadSession();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!target) return <RouteLoader />;
  if (target === "landing") return <LandingPage />;
  return <Navigate to={target} replace />;
}

function KioskPreferencesRedirect() {
  const location = useLocation();

  return (
    <Navigate
      to={{
        pathname: "/kiosk/operacoes",
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
}

function App() {
  const [navbarOpen, setNavbarOpen] = useState(false);
  const handleSwipedRight = useCallback((eventData) => {
    if (isInsideScrollableContainer(eventData?.event)) return;
    setNavbarOpen(true);
  }, []);

  const handlers = useSwipeable({
    onSwipedRight: handleSwipedRight,
    onSwipedLeft: () => setNavbarOpen(false),
    delta: 30,
    // ...config,
  });

  return (
    <div className="App" {...handlers}>
      <SeoHead />
      <Suspense fallback={<RouteLoader />}>
        <Routes>
          {!maintenance ||
          (maintenance && Cookies.get("maintenance") === "true") ? (
            <>
              <Route path="/teste" element={<LandingPage />} />
              {import.meta.env.DEV && <Route path="/teste/novo-endereco" element={<AddressMigrationPreview />} />}
              <Route path="/teste/orbital" element={<OrbitalDemo />} />
              <Route
                path="/teste/constelacao-orizom"
                element={<ConstellationDemo />}
              />
              <Route path="/" element={<RootRoute />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/cadastro" element={<Signup />} />
              <Route path="/logout" element={<Logout />} />
              <Route path="/termos-de-uso" element={<TermsPage />} />
              <Route path="/politica-de-privacidade" element={<PrivacyPage />} />
              <Route path="/privacidade" element={<PrivacyPage />} />

              <Route path="/erro-bling" element={<BlingError />} />
              <Route path="/purchase" element={<Purchase />} />
              <Route path="/catalogo" element={<ExternalCatalog />} />
              <Route path="/zpluma" element={<ZPLuma />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/kiosk/operacoes"
                element={<OperationsTvDashboard kioskMode />}
              />
              <Route
                path="/kiosk/preferences"
                element={<KioskPreferencesRedirect />}
              />

              <Route path="/admin/*" element={<AdminDomainRoutes />} />
              <Route
                path="/*"
                element={
                  <UserDomainRoutes
                    navbarOpen={navbarOpen}
                    setNavbarOpen={setNavbarOpen}
                  />
                }
              />
            </>
          ) : (
            <Route path="*" element={<Maintenance />} />
          )}
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
